package dev.lingvo.shared.screenshots.design.derive

import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.SizeRule
import dev.lingvo.shared.screenshots.doc.Board
import dev.lingvo.shared.screenshots.doc.BoardDerivation
import dev.lingvo.shared.screenshots.doc.CanvasSize
import dev.lingvo.shared.screenshots.doc.DecodeResult
import dev.lingvo.shared.screenshots.doc.DesignCodec
import dev.lingvo.shared.screenshots.doc.ScreenshotDesign
import dev.lingvo.shared.screenshots.doc.StoreDeviceClassRule
import dev.lingvo.shared.screenshots.frames.VectorFrames
import dev.lingvo.shared.screenshots.geom.safeArea
import dev.lingvo.shared.screenshots.render.upgradeLocaleOverrides
import dev.lingvo.shared.screenshots.scene.SceneInput
import dev.lingvo.shared.screenshots.scene.Scenes

/**
 * The Play feature graphic is one fixed 1024x500 banner designed on its own (one slot, no device frame), so a
 * multi-slot store layout cannot be derived into it or from it.
 */
val DeviceClass.resizable: Boolean get() = this != DeviceClass.PLAY_FEATURE_GRAPHIC

/** Shared geometry has no clock, random identity, storage or template reapplication. */
object MagicResize {
    /** Independent target boards are per-device overrides and always take precedence. */
    fun derive(design: ScreenshotDesign, sourceDevice: String, target: DeviceClass): ScreenshotDesign {
        require(target.platform == design.platform) { "derive_platform_mismatch" }
        require(target.resizable && sourceDevice != DeviceClass.PLAY_FEATURE_GRAPHIC.id) { "derive_device_not_resizable" }
        val source = requireNotNull(design.boards.firstOrNull { it.deviceClass == sourceDevice }) { "derive_source_missing" }
        require(source.derivation == null) { "derive_source_linked" }
        if (sourceDevice == target.id) return design
        val previous = design.boards.firstOrNull { it.deviceClass == target.id }
        if (previous != null && previous.derivation?.sourceDevice != sourceDevice) return design
        val canvas = targetCanvas(target, source.canvas)
        val occupied = design.boards.filterNot { it === previous }
        val ids = occupied.flatMap { it.pages }.flatMap { flattenResizeLayers(it.layers) }.map { it.base.id }.toMutableSet()
        var nextId = 0
        fun fresh(): String {
            var id: String
            do { id = "d-${target.id}-${nextId++}" } while (!ids.add(id))
            return id
        }
        val layerIds = source.pages.flatMap { flattenResizeLayers(it.layers) }.associate { it.base.id to fresh() }
        val sx = canvas.width.toDouble() / source.canvas.width
        val sy = canvas.height.toDouble() / source.canvas.height
        var boardId = previous?.id ?: "derived-${target.id}"
        var suffix = 0
        while (occupied.any { it.id == boardId }) boardId = "derived-${target.id}-${++suffix}"
        val shell = Board(boardId, target.id, canvas, emptyList(), source.mirrorRtl,
            source.safeArea?.let { it.copy(top = it.top * sy, bottom = it.bottom * sy, left = it.left * sx, right = it.right * sx) },
            BoardDerivation(sourceDevice))
        val frame = VectorFrames.defaultFor(target)
        val board = shell.copy(pages = source.pages.map { page ->
            val placements = Scenes.arrange(SceneInput(canvas, page.slotIds.size, safeArea(shell, frame), page.scene,
                frame, canvas.height.toDouble() / canvas.width)).associateBy { it.role to it.roleSlot }
            resizePage(page, sx, sy, frame, layerIds, placements, scaleFits = design.schemaVersion >= 3)
        })
        val candidate = design.copy(schemaVersion = maxOf(design.schemaVersion, 2),
            boards = if (previous == null) design.boards + board else design.boards.map { if (it === previous) board else it })
        return when (val checked = DesignCodec.decode(DesignCodec.encode(candidate), StoreDeviceClassRule)) {
            is DecodeResult.Ok -> checked.design
            is DecodeResult.Refused -> throw IllegalArgumentException(checked.issues.first().code.name)
        }
    }

    /** Selection order is canonical, so request ordering cannot change IDs, budgets or partial outcomes. */
    fun devices(design: ScreenshotDesign, source: String, targets: List<String>): DeriveDevicesResult {
        require(targets.isNotEmpty() && targets.size <= DeviceClass.entries.size && targets.distinct().size == targets.size) { "derive_invalid_targets" }
        require(design.boards.any { it.deviceClass == source && it.derivation == null }) { "derive_source_missing" }
        var document = design
        val outcomes = targets.sorted().map { raw ->
            val target = DeviceClass.fromId(raw)?.takeIf { it.id == raw }
            if (target == null || target.platform != design.platform) {
                DeriveDeviceOutcome(raw.take(40), DeriveDeviceStatus.REFUSED, "derive_platform_mismatch")
            } else try {
                val old = document.boards.firstOrNull { it.deviceClass == raw }
                val next = derive(document, source, target)
                val status = when {
                    old != null && raw != source && old.derivation?.sourceDevice != source -> DeriveDeviceStatus.OVERRIDE
                    next == document -> DeriveDeviceStatus.UNCHANGED
                    else -> DeriveDeviceStatus.DERIVED
                }
                document = next
                DeriveDeviceOutcome(raw, status)
            } catch (failure: IllegalArgumentException) {
                DeriveDeviceOutcome(raw, DeriveDeviceStatus.REFUSED, failure.message?.takeIf { CODE.matches(it) } ?: "derive_invalid")
            }
        }
        return DeriveDevicesResult(document, outcomes)
    }

    /** Compares real board content with the saved base; submitted metadata cannot create a link. */
    fun reconcile(previous: ScreenshotDesign?, submitted: ScreenshotDesign): ScreenshotDesign {
        // A compatibility-only schema adoption is not an owner's independent edit of every derived board.
        val comparable = previous?.let { if (it.schemaVersion < 3 && submitted.schemaVersion >= 3) upgradeLocaleOverrides(it) else it }
        var result = submitted.copy(boards = submitted.boards.map { board ->
            val old = comparable?.boards?.firstOrNull { it.deviceClass == board.deviceClass }
            board.copy(derivation = old?.derivation?.takeIf { link ->
                board == old && submitted.boards.any { it.deviceClass == link.sourceDevice && it.derivation == null }
            })
        })
        // Sources are independent by validation, so one deterministic pass updates every descendant.
        for (board in result.boards.toList()) {
            val link = board.derivation ?: continue
            val previousTarget = comparable?.boards?.firstOrNull { it.deviceClass == board.deviceClass }
            val previousSource = comparable?.boards?.firstOrNull { it.deviceClass == link.sourceDevice }
            val source = result.boards.firstOrNull { it.deviceClass == link.sourceDevice }
            // Compatibility metadata alone must not reflow saved locale typography under a newer resize policy.
            if (previousTarget == board && previousSource == source) continue
            result = derive(result, link.sourceDevice, requireNotNull(DeviceClass.fromId(board.deviceClass)))
        }
        return result
    }

    /** A save can refresh linked boards while the owner keeps typing; untouched boards must adopt that acknowledgement. */
    fun mergeAcknowledged(sent: ScreenshotDesign, local: ScreenshotDesign, canonical: ScreenshotDesign): ScreenshotDesign {
        val before = sent.boards.associateBy { it.deviceClass }
        val saved = canonical.boards.associateBy { it.deviceClass }
        return local.copy(schemaVersion = maxOf(local.schemaVersion, canonical.schemaVersion), boards = local.boards.map { board ->
            if (board == before[board.deviceClass]) saved[board.deviceClass] ?: board else board
        })
    }

    private val CODE = Regex("^[A-Za-z_]{1,64}$")
}

/** Pure batch output allows the server and preview to share per-device refusal semantics. */
data class DeriveDevicesResult(val document: ScreenshotDesign, val devices: List<DeriveDeviceOutcome>)

/** Chooses a legal deterministic store canvas, preserving orientation where the store permits it. */
fun targetCanvas(device: DeviceClass, source: CanvasSize): CanvasSize {
    val landscape = source.width > source.height
    return when (val rule = device.spec.storeRule) {
        is SizeRule.Exact -> rule.sizes.first().let {
            if (landscape && rule.rotatable && it.width < it.height) CanvasSize(it.height, it.width)
            else CanvasSize(it.width, it.height)
        }
        is SizeRule.Range -> {
            val aspect = rule.aspects.firstOrNull()?.let { if (landscape && it.width < it.height) it.rotated() else it }
            if (aspect != null) {
                val short = minOf(aspect.width, aspect.height)
                val scale = ((maxOf(rule.minSide, if (device == DeviceClass.PLAY_WEAR) 512 else 1080) + short - 1) / short)
                    .coerceAtMost(rule.maxSide / maxOf(aspect.width, aspect.height))
                CanvasSize(aspect.width * scale, aspect.height * scale)
            } else if (landscape) CanvasSize(1920, 1080) else CanvasSize(1080, 1920)
        }
    }
}
